/**
 * Vendored copy of Payload's Lexical LinkNode + AutoLinkNode.
 *
 * Payload (`@payloadcms/richtext-lexical`) ships custom Lexical nodes for its
 * rich-text links. Vanilla Lexical has no `link` node at all, and the stock
 * `@lexical/link` LinkNode serializes a different shape (no `fields`, no `id`,
 * no `version` migration) — so round-tripping a Payload `content` tree through
 * vanilla Lexical would silently drop the `fields`/`id` payload and corrupt a
 * saved article. These vendored classes reproduce Payload's serialization
 * contract exactly so `serialize(parse(tree))` deep-equals `tree`.
 *
 * Source of truth (do not drift):
 *   @payloadcms/richtext-lexical/dist/features/link/nodes/LinkNode.js
 *   @payloadcms/richtext-lexical/dist/features/link/nodes/AutoLinkNode.js
 *
 * Stripped vs. Payload source: DOM-import (`importDOM`/`$convertAnchorElement`)
 * is omitted — round-trip + editing never parse from pasted HTML anchors, only
 * from serialized JSON. `createDOM`/`updateDOM`/inline behaviour is kept so the
 * node renders in the live editor (T5). `TOGGLE_LINK_COMMAND`/`$toggleLink` are
 * kept for the link-editing toolbar.
 */
import { addClassNamesToElement } from "@lexical/utils"
import ObjectID from "bson-objectid"
import {
  $applyNodeReplacement,
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  createCommand,
  ElementNode,
  type DOMConversionMap,
  type EditorConfig,
  type LexicalCommand,
  type LexicalNode,
  type LexicalUpdateJSON,
  type NodeKey,
  type RangeSelection,
  type SerializedElementNode,
  type Spread,
} from "lexical"

const SUPPORTED_URL_PROTOCOLS = new Set<string>([
  "http:",
  "https:",
  "mailto:",
  "sms:",
  "tel:",
])

/**
 * The `fields` blob Payload stores on a link. `linkType` toggles whether `url`
 * (custom) or `doc` (internal) is the live half; the dormant half is deleted on
 * export. Everything else is config-driven and varies by Payload setup, so the
 * extra keys are kept open via an index signature rather than guessed.
 */
export type LinkFields = {
  doc?: {
    relationTo: string
    value: number | string
  } | null
  linkType?: "custom" | "internal"
  newTab?: boolean
  url?: string
  [key: string]: unknown
}

export type SerializedLinkNode = Spread<
  {
    fields: LinkFields
    id?: string
    type: "link"
    version: 3
  },
  SerializedElementNode
>

/**
 * The shape `importJSON` actually receives off the wire, shared by LinkNode and
 * its AutoLinkNode subclass. Older Payload exports carry `version` 1/2 and
 * trigger the migration shims, so the version field is widened from the literal
 * export type, and `type` is widened to the union so the static `importJSON`
 * override on AutoLinkNode stays signature-compatible with the base.
 */
type IncomingLinkNode = Omit<SerializedLinkNode, "type" | "version"> & {
  type: "autolink" | "link"
  version: number
}

export type SerializedAutoLinkNode = Spread<
  {
    type: "autolink"
    version: 2
  },
  Omit<SerializedLinkNode, "id" | "type" | "version">
>

export type LinkPayload = {
  fields: LinkFields
  selectedNodes?: LexicalNode[]
  text?: null | string
}

/** @noInheritDoc */
export class LinkNode extends ElementNode {
  __fields: LinkFields
  __id?: string

  constructor({
    id,
    fields = { linkType: "custom", newTab: false },
    key,
  }: {
    fields?: LinkFields
    id?: string
    key?: NodeKey
  }) {
    super(key)
    this.__fields = fields
    this.__id = id
  }

  static clone(node: LinkNode): LinkNode {
    return new this({
      id: node.__id,
      fields: node.__fields,
      key: node.__key,
    })
  }

  static getType(): string {
    return "link"
  }

  static importDOM(): DOMConversionMap | null {
    return null
  }

  static importJSON(serializedNode: IncomingLinkNode): LinkNode {
    const node = $createLinkNode({}).updateFromJSON(serializedNode)
    /** @todo remove this in 4.0 */
    if (
      serializedNode.version === 1 &&
      typeof serializedNode.fields?.doc?.value === "object" &&
      (serializedNode.fields.doc.value as { id?: unknown } | null)?.id
    ) {
      serializedNode.fields.doc!.value = (
        serializedNode.fields.doc!.value as unknown as { id: number | string }
      ).id
      serializedNode.version = 2
    }
    if (serializedNode.version === 2 && !serializedNode.id) {
      serializedNode.id = new ObjectID().toHexString()
      serializedNode.version = 3
    }
    return node
  }

  override canBeEmpty(): false {
    return false
  }

  override canInsertTextAfter(): false {
    return false
  }

  override canInsertTextBefore(): false {
    return false
  }

  override createDOM(config: EditorConfig): HTMLAnchorElement {
    const element = document.createElement("a")
    if (this.__fields?.linkType === "custom") {
      element.href = this.sanitizeUrl(this.__fields.url ?? "")
    }
    if (this.__fields?.newTab ?? false) {
      element.target = "_blank"
    }
    if (
      this.__fields?.newTab === true &&
      this.__fields?.linkType === "custom"
    ) {
      element.rel = manageRel(element.rel, "add", "noopener")
    }
    addClassNamesToElement(element, config.theme.link)
    return element
  }

  override exportJSON(): SerializedLinkNode | SerializedAutoLinkNode {
    const fields = this.getFields()
    if (fields?.linkType === "internal") {
      delete fields.url
    } else if (fields?.linkType === "custom") {
      delete fields.doc
    }
    const returnObject: SerializedLinkNode = {
      ...super.exportJSON(),
      type: "link",
      fields,
      version: 3,
    }
    const id = this.getID()
    if (id) {
      returnObject.id = id
    }
    return returnObject
  }

  override extractWithChild(
    _child: LexicalNode,
    selection: RangeSelection
  ): boolean {
    if (!$isRangeSelection(selection)) {
      return false
    }
    const anchorNode = selection.anchor.getNode()
    const focusNode = selection.focus.getNode()
    return (
      this.isParentOf(anchorNode) &&
      this.isParentOf(focusNode) &&
      selection.getTextContent().length > 0
    )
  }

  getFields(): LinkFields {
    return this.getLatest().__fields
  }

  getID(): string | undefined {
    return this.getLatest().__id
  }

  override insertNewAfter(
    selection: RangeSelection,
    restoreSelection = true
  ): LexicalNode | null {
    const element = this.getParentOrThrow().insertNewAfter(
      selection,
      restoreSelection
    )
    if ($isElementNode(element)) {
      const linkNode = $createLinkNode({ fields: this.__fields })
      element.append(linkNode)
      return linkNode
    }
    return null
  }

  override isInline(): true {
    return true
  }

  sanitizeUrl(url: string): string {
    try {
      const parsedUrl = new URL(url)
      if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
        return "about:blank"
      }
    } catch {
      return "https://"
    }
    return url
  }

  setFields(fields: LinkFields): this {
    const writable = this.getWritable()
    writable.__fields = fields
    return writable
  }

  setID(id: string | undefined): this {
    const writable = this.getWritable()
    writable.__id = id
    return writable
  }

  override updateDOM(prevNode: LinkNode, anchor: HTMLAnchorElement): boolean {
    const url = this.__fields?.url
    const newTab = this.__fields?.newTab
    if (
      url != null &&
      url !== prevNode.__fields?.url &&
      this.__fields?.linkType === "custom"
    ) {
      anchor.href = url
    }
    if (
      this.__fields?.linkType === "internal" &&
      prevNode.__fields?.linkType === "custom"
    ) {
      anchor.removeAttribute("href")
    }
    if (anchor.rel == null) {
      anchor.rel = ""
    }
    if (newTab !== prevNode.__fields?.newTab) {
      if (newTab ?? false) {
        anchor.target = "_blank"
        if (this.__fields?.linkType === "custom") {
          anchor.rel = manageRel(anchor.rel, "add", "noopener")
        }
      } else {
        anchor.removeAttribute("target")
        anchor.rel = manageRel(anchor.rel, "remove", "noopener")
      }
    }
    return false
  }

  override updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedLinkNode>
  ): this {
    return super
      .updateFromJSON(serializedNode)
      .setFields(serializedNode.fields)
      .setID(serializedNode.id)
  }
}

// Custom node type to override `canInsertTextAfter` that will
// allow typing within the link.
export class AutoLinkNode extends LinkNode {
  static override clone(node: AutoLinkNode): AutoLinkNode {
    return new this({
      id: "",
      fields: node.__fields,
      key: node.__key,
    })
  }

  static override getType(): string {
    return "autolink"
  }

  static override importDOM(): DOMConversionMap | null {
    return null
  }

  static override importJSON(serializedNode: IncomingLinkNode): AutoLinkNode {
    const node = $createAutoLinkNode({}).updateFromJSON(serializedNode)
    /** @todo remove in 4.0 */
    if (
      serializedNode.version === 1 &&
      typeof serializedNode.fields?.doc?.value === "object" &&
      (serializedNode.fields.doc.value as { id?: unknown } | null)?.id
    ) {
      serializedNode.fields.doc!.value = (
        serializedNode.fields.doc!.value as unknown as { id: number | string }
      ).id
      serializedNode.version = 2
    }
    return node
  }

  override exportJSON(): SerializedAutoLinkNode {
    const serialized = super.exportJSON()
    return {
      type: "autolink",
      children: serialized.children,
      direction: serialized.direction,
      fields: serialized.fields,
      format: serialized.format,
      indent: serialized.indent,
      version: 2,
    }
  }

  override insertNewAfter(
    selection: RangeSelection,
    restoreSelection = true
  ): LexicalNode | null {
    const element = this.getParentOrThrow().insertNewAfter(
      selection,
      restoreSelection
    )
    if ($isElementNode(element)) {
      const linkNode = $createAutoLinkNode({ fields: this.__fields })
      element.append(linkNode)
      return linkNode
    }
    return null
  }

  override updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedAutoLinkNode>
  ): this {
    return super
      .updateFromJSON(serializedNode as LexicalUpdateJSON<SerializedLinkNode>)
      .setFields(serializedNode.fields)
  }
}

export function $createLinkNode({
  id,
  fields,
}: {
  fields?: LinkFields
  id?: string
}): LinkNode {
  return $applyNodeReplacement(
    new LinkNode({
      id: id ?? new ObjectID().toHexString(),
      fields,
    })
  )
}

export function $isLinkNode(
  node: LexicalNode | null | undefined
): node is LinkNode {
  return node instanceof LinkNode
}

export function $createAutoLinkNode({
  fields,
}: {
  fields?: LinkFields
}): AutoLinkNode {
  return $applyNodeReplacement(new AutoLinkNode({ id: "", fields }))
}

export function $isAutoLinkNode(
  node: LexicalNode | null | undefined
): node is AutoLinkNode {
  return node instanceof AutoLinkNode
}

export const TOGGLE_LINK_COMMAND: LexicalCommand<LinkPayload | null> =
  createCommand("TOGGLE_LINK_COMMAND")

export function $toggleLink(payload: LinkPayload | null): void {
  const selection = $getSelection()
  if (
    !$isRangeSelection(selection) &&
    (payload === null || !payload.selectedNodes?.length)
  ) {
    return
  }
  const nodes = $isRangeSelection(selection)
    ? selection.extract()
    : payload === null
      ? []
      : (payload.selectedNodes ?? [])
  if (payload === null) {
    // Remove LinkNodes
    nodes.forEach((node) => {
      const parent = node.getParent()
      if ($isLinkNode(parent)) {
        const children = parent.getChildren()
        children.forEach((child) => {
          parent.insertBefore(child)
        })
        parent.remove()
      }
    })
    return
  }
  // Add or merge LinkNodes
  if (nodes.length === 1) {
    const firstNode = nodes[0]
    const linkNode = $isLinkNode(firstNode)
      ? firstNode
      : $getLinkAncestor(firstNode)
    if (linkNode !== null) {
      linkNode.setFields(payload.fields)
      if (payload.text != null && payload.text !== linkNode.getTextContent()) {
        linkNode.append($createTextNode(payload.text))
        linkNode.getChildren().forEach((child) => {
          if (child !== linkNode.getLastChild()) {
            child.remove()
          }
        })
      }
      return
    }
  }
  let prevParent: ElementNode | LinkNode | null = null
  let linkNode: LinkNode | null = null
  nodes.forEach((node) => {
    const parent = node.getParent()
    if (
      parent === linkNode ||
      parent === null ||
      ($isElementNode(node) && !node.isInline())
    ) {
      return
    }
    if ($isLinkNode(parent)) {
      linkNode = parent
      parent.setFields(payload.fields)
      if (payload.text != null && payload.text !== parent.getTextContent()) {
        parent.append($createTextNode(payload.text))
        parent.getChildren().forEach((child) => {
          if (child !== parent.getLastChild()) {
            child.remove()
          }
        })
      }
      return
    }
    if (!parent.is(prevParent)) {
      prevParent = parent
      linkNode = $createLinkNode({ fields: payload.fields })
      if ($isLinkNode(parent)) {
        if (node.getPreviousSibling() === null) {
          parent.insertBefore(linkNode)
        } else {
          parent.insertAfter(linkNode)
        }
      } else {
        node.insertBefore(linkNode)
      }
    }
    if ($isLinkNode(node)) {
      if (node.is(linkNode)) {
        return
      }
      if (linkNode !== null) {
        const children = node.getChildren()
        linkNode.append(...children)
      }
      node.remove()
      return
    }
    if (linkNode !== null) {
      linkNode.append(node)
    }
  })
}

function $getLinkAncestor(node: LexicalNode): LinkNode | null {
  let parent: LexicalNode | null = node
  while (parent !== null) {
    parent = parent.getParent()
    if (parent === null || $isLinkNode(parent)) {
      break
    }
  }
  return $isLinkNode(parent) ? parent : null
}

function manageRel(
  input: string,
  action: "add" | "remove",
  value: string
): string {
  let result: string
  let mutableInput = `${input}`
  if (action === "add") {
    if (mutableInput.includes(value)) {
      const re = new RegExp(value, "g")
      mutableInput = mutableInput.replace(re, "").trim()
    }
    mutableInput = mutableInput.trim()
    result = mutableInput.length === 0 ? `${value}` : `${mutableInput} ${value}`
  } else {
    const re = new RegExp(value, "g")
    result = mutableInput.replace(re, "").trim()
  }
  return result
}
